import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import {
  ALL_SECTOR_TYPES,
  formatSource,
  type SectorIntelFilters,
  type SectorType,
} from './sectorIntel.types';
import { CanonicalSelect } from '../SectorTaxonomy/CanonicalSelect';
import { useTaxonomyList } from '../SectorTaxonomy/useSectorTaxonomy';

interface Props {
  filters: SectorIntelFilters;
  availableSources: string[];
  onChange: (next: SectorIntelFilters) => void;
  onExport: () => void;
  exporting: boolean;
}

// 필터 바: 섹터 유형 / 소스 / 섹터명 / active_only / 엑셀 다운로드
export function SectorIntelFilterBar({
  filters,
  availableSources,
  onChange,
  onExport,
  exporting,
}: Props): JSX.Element {
  // 섹터명 입력 debounce 300ms
  const [nameInput, setNameInput] = useState(filters.sectorName);
  useEffect(() => {
    setNameInput(filters.sectorName);
  }, [filters.sectorName]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (nameInput !== filters.sectorName) {
        onChange({ ...filters, sectorName: nameInput, page: 0 });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // onChange/filters 는 toggle 과 동기화되므로 의도적으로 nameInput 변경에만 반응.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameInput]);

  // 종목명 입력 debounce 300ms — 섹터명과 동일 패턴.
  const [stockNameInput, setStockNameInput] = useState(filters.stockName);
  useEffect(() => {
    setStockNameInput(filters.stockName);
  }, [filters.stockName]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (stockNameInput !== filters.stockName) {
        onChange({ ...filters, stockName: stockNameInput, page: 0 });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockNameInput]);

  function toggleSectorType(t: SectorType): void {
    const next = filters.sectorTypes.includes(t)
      ? filters.sectorTypes.filter((x) => x !== t)
      : [...filters.sectorTypes, t];
    onChange({ ...filters, sectorTypes: next, page: 0 });
  }

  function toggleSource(s: string): void {
    const next = filters.sources.includes(s)
      ? filters.sources.filter((x) => x !== s)
      : [...filters.sources, s];
    onChange({ ...filters, sources: next, page: 0 });
  }

  // Canonical 섹터 목록 — 활성만(기본). 메타 L1 은 CanonicalSelect 가 자체 제외.
  const taxonomy = useTaxonomyList({ isActive: true });

  return (
    <div className="pca-filter">
      <div className="pca-filter__group">
        <label className="pca-filter__label">섹터 유형</label>
        <div className="pca-chip-row">
          {ALL_SECTOR_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={
                'pca-chip' +
                (filters.sectorTypes.includes(t) ? ' pca-chip--active' : '')
              }
              onClick={() => toggleSectorType(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="pca-filter__group" style={{ flex: 1, minWidth: 260 }}>
        <label className="pca-filter__label">소스</label>
        <div className="pca-chip-row">
          {availableSources.length === 0 && (
            <span className="pca-muted">로딩…</span>
          )}
          {availableSources.map((s) => (
            <button
              key={s}
              type="button"
              className={
                'pca-chip' +
                (filters.sources.includes(s) ? ' pca-chip--active' : '')
              }
              onClick={() => toggleSource(s)}
            >
              {formatSource(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label" htmlFor="si-name">
          섹터명
        </label>
        <input
          id="si-name"
          className="pca-input"
          type="text"
          placeholder="예: 반도체"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          style={{ minWidth: 200 }}
        />
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label" htmlFor="si-stock">
          종목명
        </label>
        <input
          id="si-stock"
          className="pca-input"
          type="text"
          placeholder="예: 삼성전자"
          value={stockNameInput}
          onChange={(e) => setStockNameInput(e.target.value)}
          style={{ minWidth: 180 }}
        />
      </div>

      <div className="pca-filter__group" style={{ minWidth: 260 }}>
        <label className="pca-filter__label" htmlFor="si-canonical">
          최종 섹터 (canonical)
        </label>
        <CanonicalSelect
          inputId="si-canonical"
          ariaLabel="최종 섹터 (canonical)"
          value={filters.canonicalSectorId}
          onChange={(v) =>
            onChange({ ...filters, canonicalSectorId: v, page: 0 })
          }
          options={(taxonomy.data ?? []).filter((t) => (t.mappingCount ?? 0) > 0)}
          disabled={taxonomy.isLoading}
        />
      </div>

      <div className="pca-filter__group">
        <label className="pca-filter__label">현재 유효만</label>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={filters.activeOnly}
            onChange={(e) =>
              onChange({ ...filters, activeOnly: e.target.checked, page: 0 })
            }
          />
          active only
        </label>
      </div>

      <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
        <label className="pca-filter__label">&nbsp;</label>
        <button
          type="button"
          className="pca-btn pca-btn--primary"
          onClick={onExport}
          disabled={exporting}
        >
          {exporting ? (
            <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
          ) : (
            <Download size={14} strokeWidth={1.75} />
          )}
          {exporting ? '다운로드 중…' : '엑셀 다운로드'}
        </button>
      </div>
    </div>
  );
}
